import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// Auth is carried by HttpOnly cookies (access_token / refresh_token) set by the backend.
// No tokens are ever stored in JS-accessible storage.
export function authFetch(url, options = {}) {
  return fetch(url, { ...options, credentials: 'include' });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/auth/me`);
      if (res.ok) {
        setUser(await res.json());
      } else {
        // Try refresh via HttpOnly refresh_token cookie
        const rRes = await fetch(`${API}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (rRes.ok) {
          const meRes = await authFetch(`${API}/api/auth/me`);
          if (meRes.ok) {
            setUser(await meRes.json());
            setChecking(false);
            return;
          }
        }
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(formatError(err.detail));
    }
    const data = await res.json();
    setUser({ email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/api/auth/logout`, { method: 'POST' });
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

function formatError(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}
